import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { STORAGE_KEYS, DEFAULT_CURRENCY } from '@/lib/config/constants';
import { uid } from '@/lib/utils/id';
import type { CartItem, Customer, Product, RentalDurationUnit } from '@/types/domain';
import type { ID } from '@/types/common';
import { sameLine, lineUnitPrice, maxUnitsForLine, defaultDurationUnit, defaultHirePeriod, hirePriceFor, hasHireRates, priceForCurrency } from './cartMath';

export * from './cartMath';

export interface CartTotals {
  subtotal: number;
  discountTotal: number;
  taxTotal: number;
  total: number;

  passthroughTotal: number;
  itemCount: number;
}

export type DiscountType = 'amount' | 'percent';

interface CartState {
  items: CartItem[];
  customer: Customer | null;
  globalDiscount: number;
  globalDiscountType: DiscountType;
  note: string;
  currency: string;

  addProduct(product: Product, quantity?: number): void;
  addCustomLine(name: string, unitPrice: number, quantity?: number, taxRate?: number): void;
  removeItem(productId: ID): void;
  updateQty(productId: ID, qty: number, maxStock?: number): void;
  updateLineDiscount(productId: ID, discount: number): void;
  updateLinePrice(productId: ID, unitPrice: number): void;
  updateDuration(productId: ID, duration: number | undefined, durationUnit?: RentalDurationUnit): void;
  setGlobalDiscount(value: number): void;
  setGlobalDiscountType(type: DiscountType): void;
  setCustomer(customer: Customer | null): void;
  setNote(note: string): void;
  setCurrency(currency: string): void;
  clear(): void;
  totals(taxInclusive?: boolean, defaultTaxRate?: number): CartTotals;
}

function totalsFrom(
  items: CartItem[],
  globalDiscount: number,
  globalDiscountType: DiscountType,
  taxInclusive: boolean,
  defaultTaxRate: number,
): CartTotals {
  const itemCount = items.reduce((s, i) => s + i.quantity, 0);
  let subtotal = 0;
  let lineDiscountTotal = 0;
  let taxTotal = 0;

  let passthroughGross = 0;

  for (const item of items) {
    const taxRate = item.taxRate || defaultTaxRate;
    const dur = item.offerType === 'rent' && item.duration && item.duration > 0 ? item.duration : 1;
    const lineGross = lineUnitPrice(item) * item.quantity * dur;
    const lineNet = Math.max(0, lineGross - item.discount);
    if (taxInclusive) {
      const tax = (lineNet * taxRate) / 100;
      subtotal += lineNet - tax;
      taxTotal += tax;
      if (item.passthrough) passthroughGross += lineNet;
    } else {
      subtotal += lineNet;
      taxTotal += (lineNet * taxRate) / 100;
      if (item.passthrough) passthroughGross += lineNet + (lineNet * taxRate) / 100;
    }
    lineDiscountTotal += item.discount;
  }

  const grossBeforeGlobal = subtotal + taxTotal;
  const globalDiscountAmount =
    globalDiscountType === 'percent'
      ? round2((subtotal * Math.min(Math.max(0, globalDiscount), 100)) / 100)
      : Math.min(Math.max(0, globalDiscount), grossBeforeGlobal);

  const total = Math.max(0, grossBeforeGlobal - globalDiscountAmount);
  return {
    subtotal: round2(subtotal),
    discountTotal: round2(lineDiscountTotal + globalDiscountAmount),
    taxTotal: round2(taxTotal),
    total: round2(total),
    passthroughTotal: round2(passthroughGross),
    itemCount,
  };
}

const round2 = (n: number) => Math.round(n * 100) / 100;

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],
      customer: null,
      globalDiscount: 0,
      globalDiscountType: 'amount',
      note: '',
      currency: DEFAULT_CURRENCY.code,

      addProduct: (product, quantity = 1) =>
        set((state) => {
          const baseStock = product.stockQty;
          const stockLimited = product.trackStock;
          const maxQty = stockLimited ? maxUnitsForLine(baseStock, 1) : Infinity;
          const minQty = Math.max(1, product.minSaleQty ?? 1);
          const idx = state.items.findIndex((i) => sameLine(i, product.id));
          if (idx >= 0) {
            const next = [...state.items];
            const newQty = Math.min(next[idx]!.quantity + quantity, maxQty);
            if (newQty === next[idx]!.quantity) return state;
            next[idx] = { ...next[idx]!, quantity: newQty };
            return { items: next };
          }
          const isRent = product.offerType === 'rent';
          const rates = {
            dailyPrice: product.dailyPrice,
            weeklyPrice: product.weeklyPrice,
            monthlyPrice: product.monthlyPrice,
          };
          // Hire products with explicit rates bill the default priced period,
          // not the flat product price.
          const hirePeriod = isRent ? defaultHirePeriod({ ...rates, priceSuffix: product.priceSuffix }) : null;
          const periodRate = hirePeriod ? hirePriceFor(rates, hirePeriod) : undefined;
          const kesPrice = periodRate ?? product.price;
          const altPrice =
            state.currency !== DEFAULT_CURRENCY.code
              ? product.currencyPrices?.find((c) => c.currency === state.currency)?.amount
              : undefined;
          const price = altPrice ?? kesPrice;
          const item: CartItem = {
            productId: product.id,
            sku: product.sku,
            name: product.name,
            description: product.description,
            unitPrice: price,
            basePrice: price,
            kesPrice,
            currencyPrices: product.currencyPrices,
            quantity: Math.min(Math.max(quantity, minQty), maxQty),
            taxRate: product.taxRate,
            discount: 0,
            imageUrl: product.imageUrl,
            trackStock: stockLimited,
            stockQty: stockLimited ? baseStock : undefined,
            minSaleQty: minQty > 1 ? minQty : undefined,
            offerType: product.offerType,
            priceSuffix: product.priceSuffix,
            dailyPrice: product.dailyPrice,
            weeklyPrice: product.weeklyPrice,
            monthlyPrice: product.monthlyPrice,
            duration: isRent ? 1 : undefined,
            durationUnit: isRent
              ? hirePeriod ?? defaultDurationUnit(product.priceSuffix)
              : undefined,
          };
          return { items: [...state.items, item] };
        }),

      addCustomLine: (name, unitPrice, quantity = 1, taxRate = 0) =>
        set((state) => {
          const item: CartItem = {
            productId: uid('custom'),
            sku: 'CUSTOM',
            name: name.trim() || 'Custom item',
            unitPrice: Math.max(0, unitPrice),
            basePrice: Math.max(0, unitPrice),
            kesPrice: Math.max(0, unitPrice),
            quantity: Math.max(1, Math.floor(quantity) || 1),
            taxRate,
            discount: 0,
            trackStock: false,

            passthrough: true,
          };
          return { items: [...state.items, item] };
        }),

      removeItem: (productId) =>
        set((state) => ({ items: state.items.filter((i) => !sameLine(i, productId)) })),

      updateQty: (productId, qty, maxStock) =>
        set((state) => ({
          items: state.items
            .map((i) => {
              if (!sameLine(i, productId)) return i;
              let q = Math.max(0, qty);
              const min = i.minSaleQty ?? 1;
              if (q > 0 && q < min) q = min;
              return { ...i, quantity: Math.min(q, maxStock ?? Infinity) };
            })
            .filter((i) => i.quantity > 0),
        })),

      updateLineDiscount: (productId, discount) =>
        set((state) => ({
          items: state.items.map((i) =>
            sameLine(i, productId) ? { ...i, discount: Math.max(0, discount) } : i,
          ),
        })),

      updateLinePrice: (productId, unitPrice) =>
        set((state) => ({
          items: state.items.map((i) =>
            sameLine(i, productId)
              ? { ...i, unitPrice: Math.max(i.basePrice ?? 0, unitPrice) }
              : i,
          ),
        })),

      updateDuration: (productId, duration, durationUnit) =>
        set((state) => ({
          items: state.items.map((i) => {
            if (!sameLine(i, productId)) return i;
            const nextUnit = durationUnit ?? i.durationUnit;
            const next: CartItem = {
              ...i,
              duration: duration && duration > 0 ? duration : undefined,
              durationUnit: nextUnit,
            };
            // Switching the billing period on a rate-priced hire line re-bills
            // it at that period's list price.
            if (nextUnit && nextUnit !== i.durationUnit && hasHireRates(i)) {
              const rate = hirePriceFor(i, nextUnit);
              if (rate !== undefined) {
                next.unitPrice = rate;
                next.basePrice = rate;
              }
            }
            return next;
          }),
        })),

      setGlobalDiscount: (value) => set({ globalDiscount: Math.max(0, value) }),
      setGlobalDiscountType: (type) => set({ globalDiscountType: type }),
      setCustomer: (customer) => set({ customer }),
      setNote: (note) => set({ note }),
      // Switching currency re-prices every product line from the product's
      // configured amounts for that currency (falling back to the KES list
      // price). Custom lines are hand-priced and left untouched.
      setCurrency: (currency) =>
        set((state) => ({
          currency,
          items: state.items.map((i) => {
            if (i.passthrough) return i;
            const price = priceForCurrency(i, currency);
            return { ...i, unitPrice: price, basePrice: price };
          }),
        })),
      clear: () =>
        set({
          items: [],
          customer: null,
          globalDiscount: 0,
          globalDiscountType: 'amount',
          note: '',
        }),

      totals: (taxInclusive = false, defaultTaxRate = 0) =>
        totalsFrom(
          get().items,
          get().globalDiscount,
          get().globalDiscountType,
          taxInclusive,
          defaultTaxRate,
        ),
    }),
    {
      name: STORAGE_KEYS.cart,
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({
        items: s.items,
        customer: s.customer,
        globalDiscount: s.globalDiscount,
        globalDiscountType: s.globalDiscountType,
        note: s.note,
      }),
      // Currency is a per-sale choice, not a user preference: every page load
      // starts the POS at KES, even if an older session stored something else.
      merge: (persisted, current) => ({
        ...current,
        ...((persisted as Partial<typeof current> | undefined) ?? {}),
        currency: DEFAULT_CURRENCY.code,
      }),
    },
  ),
);